/**
 * SourceDataHydrator
 *
 * Business logic service for transforming source data IDs into full objects
 * for AI consumption. Implements specific hydration rules:
 * - Audiences: EXCLUDE fullInterviewResult from each Actor
 * - Tasks: Include all properties
 * - StudyCases: INCLUDE outputs subcollection
 *
 * Related: Sprint 5, FR-007
 */

import { AudienceService } from '@modules/firebase-core/services/AudienceService';
import { TaskService } from '@modules/firebase-core/services/TaskService';
import { StudyCaseService } from '@modules/firebase-core/services/StudyCaseService';
import { ReviewService } from '@modules/firebase-core/services/ReviewService';
import { RatingService } from '@modules/firebase-core/services/RatingService';
import { BigNumberService } from '@modules/firebase-core/services/BigNumberService';
import type { InsightsAnalysisDoc, InsightsCategoryPackageDoc } from '@modules/insights-analyzer/services/InsightsFirestoreService';
import type { StudyCase, Output, Review, Rating, BigNumber } from '@segmently/schema-models/entities';
import type { Audience, Actor, Task } from '@segmently/schema-models/entities';
import type { HydratedSourceData } from '@modules/shared-types/src/agent.types';

// Re-export so existing consumers keep working
export type { HydratedSourceData };

/**
 * Source data IDs (input)
 */
export interface SourceData {
  audienceIds: string[];
  taskIds: string[];
  studyCaseIds: string[];
  reviewIds: string[];
  ratingIds: string[];
  bigNumberIds: string[];
  insightIds?: string[];
}

/**
 * Actor without fullInterviewResult field
 */
export type ActorWithoutInterview = Omit<Actor, 'fullInterviewResult'>;

/**
 * Hydrated audience with actors (fullInterviewResult excluded)
 */
export interface HydratedAudience extends Audience {
  actors: ActorWithoutInterview[];
}

/**
 * Hydrated study case with outputs
 */
export interface HydratedStudyCase extends StudyCase {
  outputs: Output[];
}

/**
 * SourceDataHydrator Service
 *
 * Transforms source data IDs into full objects for AI processing.
 * Uses dependency injection for firebase-core services.
 *
 * Usage:
 * ```typescript
 * const hydrator = new SourceDataHydrator(
 *   projectId,
 *   audienceService,
 *   taskService,
 *   studyCaseService
 * );
 *
 * const hydratedData = await hydrator.hydrate({
 *   audienceIds: ['aud-1', 'aud-2'],
 *   taskIds: ['task-1'],
 *   studyCaseIds: ['study-1']
 * });
 *
 * // Result:
 * // {
 * //   audiences: [{ ...audience, actors: [...] }], // actors WITHOUT fullInterviewResult
 * //   tasks: [{ ...task }],
 * //   studyCases: [{ ...studyCase, outputs: [...] }]
 * // }
 * ```
 */
export class SourceDataHydrator {
  constructor(
    private readonly projectId: string,
    private readonly audienceService: AudienceService,
    private readonly taskService: TaskService,
    private readonly studyCaseService: StudyCaseService,
    private readonly reviewService: ReviewService,
    private readonly ratingService: RatingService,
    private readonly bigNumberService: BigNumberService
  ) {}

  /**
   * Hydrate audiences with actors
   *
   * CRITICAL: Excludes fullInterviewResult field from ALL actors
   *
   * @param audienceIds - Array of audience IDs to hydrate
   * @returns {Promise<HydratedAudience[]>} Audiences with actors (fullInterviewResult excluded)
   */
  async hydrateAudiences(audienceIds: string[]): Promise<HydratedAudience[]> {
    if (!audienceIds || audienceIds.length === 0) {
      return [];
    }

    try {
      // Load all audiences in parallel
      const audiencePromises = audienceIds.map(async (audienceId) => {
        // Get audience document
        const audience = await this.audienceService.get(audienceId);
        if (!audience) {
          console.warn(`[SourceDataHydrator] Audience ${audienceId} not found`);
          return null;
        }

        // Get actors with full subcollections (coreJobs, fears, contexts, etc.)
        const actorService = this.audienceService.getActorsService(audienceId);
        const actorsList = await actorService.list({ limit: 1000 });

        // Load subcollections for each actor (fears, desires, pains, gains, etc.)
        const actorsWithSubs = await Promise.all(
          actorsList.map(async (actor) => {
            const full = await actorService.getWithSubcollections(actor.id!);
            return full || actor;
          })
        );

        // CRITICAL: Exclude fullInterviewResult from each actor
        const actorsWithoutInterview: ActorWithoutInterview[] = actorsWithSubs.map(
          ({ fullInterviewResult, ...actor }) => actor
        );

        // Return hydrated audience with actors
        const hydratedAudience: HydratedAudience = {
          ...audience,
          actors: actorsWithoutInterview,
        };

        return hydratedAudience;
      });

      const results = await Promise.all(audiencePromises);

      // Filter out nulls (audiences not found)
      return results.filter((audience): audience is HydratedAudience => audience !== null);
    } catch (error) {
      console.error('[SourceDataHydrator] Error hydrating audiences:', error);
      throw error;
    }
  }

  /**
   * Hydrate tasks
   *
   * @param taskIds - Array of task IDs to hydrate
   * @returns {Promise<Task[]>} Tasks with all properties
   */
  async hydrateTasks(taskIds: string[]): Promise<Task[]> {
    if (!taskIds || taskIds.length === 0) {
      return [];
    }

    try {
      // Load all tasks in parallel
      const taskPromises = taskIds.map(async (taskId) => {
        const task = await this.taskService.get(taskId);
        if (!task) {
          console.warn(`[SourceDataHydrator] Task ${taskId} not found`);
          return null;
        }
        return task;
      });

      const results = await Promise.all(taskPromises);

      // Filter out nulls (tasks not found)
      return results.filter((task): task is Task => task !== null);
    } catch (error) {
      console.error('[SourceDataHydrator] Error hydrating tasks:', error);
      throw error;
    }
  }

  /**
   * Hydrate study cases with outputs
   *
   * @param studyCaseIds - Array of study case IDs to hydrate
   * @returns {Promise<HydratedStudyCase[]>} Study cases with outputs subcollection
   */
  async hydrateStudyCases(studyCaseIds: string[]): Promise<HydratedStudyCase[]> {
    if (!studyCaseIds || studyCaseIds.length === 0) {
      return [];
    }

    try {
      // Load all study cases in parallel
      const studyCasePromises = studyCaseIds.map(async (studyCaseId) => {
        // Get study case document
        const studyCase = await this.studyCaseService.get(studyCaseId);
        if (!studyCase) {
          console.warn(`[SourceDataHydrator] StudyCase ${studyCaseId} not found`);
          return null;
        }

        // Get outputs subcollection
        const outputService = this.studyCaseService.getOutputsService(studyCaseId);
        const outputs = await outputService.list({ limit: 1000 });

        // Return hydrated study case with outputs
        const hydratedStudyCase: HydratedStudyCase = {
          ...studyCase,
          outputs,
        };

        return hydratedStudyCase;
      });

      const results = await Promise.all(studyCasePromises);

      // Filter out nulls (study cases not found)
      return results.filter((studyCase): studyCase is HydratedStudyCase => studyCase !== null);
    } catch (error) {
      console.error('[SourceDataHydrator] Error hydrating study cases:', error);
      throw error;
    }
  }

  /**
   * Hydrate reviews
   *
   * @param reviewIds - Array of review IDs to hydrate
   * @returns {Promise<Review[]>} Reviews with all properties
   */
  async hydrateReviews(reviewIds: string[]): Promise<Review[]> {
    if (!reviewIds || reviewIds.length === 0) {
      return [];
    }

    try {
      // Load all reviews in parallel
      const reviewPromises = reviewIds.map(async (reviewId) => {
        const review = await this.reviewService.get(reviewId);
        if (!review) {
          console.warn(`[SourceDataHydrator] Review ${reviewId} not found`);
          return null;
        }
        return review;
      });

      const results = await Promise.all(reviewPromises);

      // Filter out nulls (reviews not found)
      return results.filter((review): review is Review => review !== null);
    } catch (error) {
      console.error('[SourceDataHydrator] Error hydrating reviews:', error);
      throw error;
    }
  }

  /**
   * Hydrate ratings
   *
   * @param ratingIds - Array of rating IDs to hydrate
   * @returns {Promise<Rating[]>} Ratings with all properties
   */
  async hydrateRatings(ratingIds: string[]): Promise<Rating[]> {
    if (!ratingIds || ratingIds.length === 0) {
      return [];
    }

    try {
      // Load all ratings in parallel
      const ratingPromises = ratingIds.map(async (ratingId) => {
        const rating = await this.ratingService.get(ratingId);
        if (!rating) {
          console.warn(`[SourceDataHydrator] Rating ${ratingId} not found`);
          return null;
        }
        return rating;
      });

      const results = await Promise.all(ratingPromises);

      // Filter out nulls (ratings not found)
      return results.filter((rating): rating is Rating => rating !== null);
    } catch (error) {
      console.error('[SourceDataHydrator] Error hydrating ratings:', error);
      throw error;
    }
  }

  /**
   * Hydrate big numbers
   *
   * @param bigNumberIds - Array of big number IDs to hydrate
   * @returns {Promise<BigNumber[]>} Big numbers with all properties
   */
  async hydrateBigNumbers(bigNumberIds: string[]): Promise<BigNumber[]> {
    if (!bigNumberIds || bigNumberIds.length === 0) {
      return [];
    }

    try {
      // Load all big numbers in parallel
      const bigNumberPromises = bigNumberIds.map(async (bigNumberId) => {
        const bigNumber = await this.bigNumberService.get(bigNumberId);
        if (!bigNumber) {
          console.warn(`[SourceDataHydrator] BigNumber ${bigNumberId} not found`);
          return null;
        }
        return bigNumber;
      });

      const results = await Promise.all(bigNumberPromises);

      // Filter out nulls (big numbers not found)
      return results.filter((bigNumber): bigNumber is BigNumber => bigNumber !== null);
    } catch (error) {
      console.error('[SourceDataHydrator] Error hydrating big numbers:', error);
      throw error;
    }
  }

  /**
   * Hydrate insights analyses
   *
   * Loads InsightsAnalysis documents by ID.
   * New strategy generation pins completed analyses that expose scoped category
   * package manifests, not only legacy parent-level aggregatedInsights.
   */
  async hydrateInsights(insightIds: string[]): Promise<InsightsAnalysisDoc[]> {
    if (!insightIds || insightIds.length === 0) {
      return [];
    }

    const { InsightsFirestoreService } = await import('@modules/insights-analyzer/services/InsightsFirestoreService');
    const service = new InsightsFirestoreService(this.projectId);
    const results: InsightsAnalysisDoc[] = [];

    for (const id of insightIds) {
      try {
        const doc = await service.get(id);
        const hasLegacyParentInsights = Boolean(doc?.aggregatedInsights);
        const hasScopedInsightPackages = Boolean(
          doc?.categoryPackages
          || doc?.categoryPackageStorage
          || doc?.categoryPackageCollection
          || doc?.perTaskPackages,
        );
        if (doc && doc.status === 'completed' && (hasLegacyParentInsights || hasScopedInsightPackages)) {
          if (!doc.aggregatedInsights && hasScopedInsightPackages) {
            const categoryPackages = await service.listCategoryPackages(
              id,
              doc.categoryPackageCollection ?? 'category_packages',
            ).catch((): InsightsCategoryPackageDoc[] => []);
            const aggregatedInsights = categoryPackages.reduce<Record<string, any[]>>((acc, pkg) => {
              const categoryInsights = pkg.aggregatedInsights?.[pkg.category ?? pkg.id];
              if (Array.isArray(categoryInsights)) acc[pkg.category ?? pkg.id] = categoryInsights;
              return acc;
            }, {});
            results.push({
              ...doc,
              aggregatedInsights,
              categoryPackages: doc.categoryPackages ?? Object.fromEntries(categoryPackages.map((pkg) => [pkg.id, pkg])),
            });
          } else {
            results.push(doc);
          }
        }
      } catch (err) {
        console.warn(`[SourceDataHydrator] Failed to load insight analysis ${id}:`, err);
      }
    }

    return results;
  }

  /**
   * Hydrate all source data types
   *
   * Loads audiences, tasks, study cases, reviews, ratings, big numbers, and insights in parallel.
   * Implements hydration rules:
   * - Audiences: exclude fullInterviewResult from actors
   * - Tasks: all properties
   * - StudyCases: include outputs subcollection
   * - Reviews: all properties
   * - Ratings: all properties
   * - BigNumbers: all properties
   *
   * @param sourceData - Source data IDs to hydrate
   * @returns {Promise<HydratedSourceData>} Fully hydrated source data
   */
  async hydrate(sourceData: SourceData): Promise<HydratedSourceData> {
    try {
      // Execute all hydrations in parallel for performance
      const [audiences, tasks, studyCases, reviews, ratings, bigNumbers, insights] = await Promise.all([
        this.hydrateAudiences(sourceData.audienceIds),
        this.hydrateTasks(sourceData.taskIds),
        this.hydrateStudyCases(sourceData.studyCaseIds),
        this.hydrateReviews(sourceData.reviewIds),
        this.hydrateRatings(sourceData.ratingIds),
        this.hydrateBigNumbers(sourceData.bigNumberIds),
        this.hydrateInsights(sourceData.insightIds ?? []),
      ]);

      return {
        audiences: audiences as unknown as HydratedSourceData['audiences'],
        tasks: tasks as unknown as HydratedSourceData['tasks'],
        studyCases: studyCases as unknown as HydratedSourceData['studyCases'],
        reviews: reviews as unknown as HydratedSourceData['reviews'],
        ratings: ratings as unknown as HydratedSourceData['ratings'],
        bigNumbers: bigNumbers as unknown as HydratedSourceData['bigNumbers'],
        insights: insights.length > 0 ? insights as unknown as HydratedSourceData['insights'] : undefined,
      };
    } catch (error) {
      console.error('[SourceDataHydrator] Error hydrating source data:', error);
      throw error;
    }
  }
}
